import React, { useState, useEffect, useCallback } from 'react';
import RichEditor from '../../components/admin/RichEditor';

// Strip absolute localhost URLs back to relative before saving to DB
const cleanHtmlForSave = (html) => {
  if (!html) return html;
  return html
    .replace(/src="http:\/\/localhost:\d+(\/uploads\/[^"]+)"/g, 'src="$1"')
    .replace(/src="http:\/\/[\d.]+:\d+(\/uploads\/[^"]+)"/g, 'src="$1"')
    .replace(/src="http:\/\/[\d.]+(\/uploads\/[^"]+)"/g, 'src="$1"');
};



import { Row, Col, Button, Form, Modal, Spinner, Table, InputGroup, Badge } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaEye, FaEyeSlash, FaCheck, FaTimes, FaStar, FaDownload } from 'react-icons/fa';
import { productAPI, newsAPI, offerAPI, sliderAPI, testimonialAPI, adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) {
    const base = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace('/api','')
      : '';
    return `${base}${url}`;
  }
  return url;
};


function PageHeader({ title, subtitle, btnLabel, onAdd }) {
  return (
    <div className="d-flex justify-content-between align-items-start mb-4">
      <div>
        <h4 style={{ fontFamily:'Space Grotesk, system-ui, sans-serif', color:'#334155', marginBottom:'0.2rem' }}>{title}</h4>
        {subtitle && <p style={{ color:'#64748b', fontSize:'0.88rem', margin:0 }}>{subtitle}</p>}
      </div>
      {onAdd && <Button className="btn-cyber d-flex align-items-center gap-2" onClick={onAdd} style={{ borderRadius:8, fontSize:'0.88rem' }}><FaPlus /> {btnLabel||'Add New'}</Button>}
    </div>
  );
}

function AdminTable({ cols, rows, loading, emptyIcon='📋', emptyText='No items yet' }) {
  return (
    <div style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:'12px', overflow:'hidden' }}>
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color:'#0284c7' }} /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>{emptyIcon}</div>
          <p style={{ color:'#64748b' }}>{emptyText}</p>
        </div>
      ) : (
        <Table responsive hover className="mb-0 admin-table">
          <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
    </div>
  );
}

function ActBtns({ onEdit, onDelete }) {
  return (
    <div className="d-flex gap-2">
      {onEdit  && <button onClick={onEdit}   style={{ padding:'0.3rem 0.6rem', border:'1px solid #e2e8f0', borderRadius:6, background:'rgba(0,212,255,0.05)', cursor:'pointer', color:'#0284c7',  fontSize:'0.82rem' }}><FaEdit /></button>}
      {onDelete && <button onClick={onDelete} style={{ padding:'0.3rem 0.6rem', border:'1px solid rgba(231,76,60,0.2)', borderRadius:6, background:'rgba(231,76,60,0.05)', cursor:'pointer', color:'#ff6b6b', fontSize:'0.82rem' }}><FaTrash /></button>}
    </div>
  );
}

function StatusBadge({ active }) {
  return <span style={{ background:active?'rgba(0,212,255,0.1)':'rgba(255,255,255,0.05)', color:active?'#0066ff':'#475569', padding:'0.15rem 0.65rem', borderRadius:100, fontSize:'0.72rem', fontWeight:700 }}>{active?'● Active':'○ Off'}</span>;
}

// ─── Products Admin ───────────────────────────────────────────────────────────
export function ProductsAdmin() {
  const [data,setData]=useState([]); const [loading,setL]=useState(true);
  const [modal,setModal]=useState(false); const [editing,setEdit]=useState(null);
  const [imgFile,setImg]=useState(null); const [saving,setSav]=useState(false);
  const EMPTY={name:'',category:'',short_desc:'',description:'',is_featured:false,is_active:true,sort_order:0,meta_title:'',meta_desc:'',meta_keywords:''};
  const [form,setForm]=useState(EMPTY);

  const load=async()=>{setL(true);try{const r=await productAPI.adminGetAll({limit:100});setData(r.data.data||[]);}catch{}setL(false);};
  useEffect(()=>{load();},[]);

  const openCreate=()=>{setEdit(null);setForm(EMPTY);setImg(null);setModal(true);};
  const openEdit=(p)=>{setEdit(p);setForm({name:p.name,category:p.category,short_desc:p.short_desc||'',description:p.description||'',is_featured:Boolean(p.is_featured),is_active:Boolean(p.is_active),sort_order:p.sort_order,meta_title:p.meta_title||'',meta_desc:p.meta_desc||'',meta_keywords:p.meta_keywords||'',cover_image:p.cover_image||''});setImg(null);setModal(true);};

  const handleSave=async()=>{
    if(!form.name||!form.category){toast.error('Name and category required');return;}
    setSav(true);
    try{
      const fd=new FormData();
      Object.entries(form).forEach(([k,v])=>{
        if(k==='is_active'||k==='is_featured'){
          fd.append(k, v?'1':'0');
        } else if(k==='description'){
          fd.append(k, cleanHtmlForSave(v));
        } else if(v!==null&&v!==''){
          fd.append(k,v);
        }
      });
      if(imgFile)fd.append('image',imgFile);
      editing?await productAPI.update(editing.id,fd):await productAPI.create(fd);
      toast.success(editing?'Product updated!':'Product created!');
      setModal(false);load();
    }catch(err){toast.error(err.response?.data?.message||'Save failed');}
    setSav(false);
  };
  const handleDelete=async(id)=>{if(!window.confirm('Delete this product?'))return;try{await productAPI.delete(id);toast.success('Product deleted');load();}catch(err){toast.error(err.response?.data?.message||'Delete failed');}};

  const CATS=['Food Management','IoT Solutions','Cyber Security','Digital Transformation','Healthcare','Other'];

  return(<>
    <PageHeader title="Products" subtitle="Manage software products and solutions" btnLabel="Add Product" onAdd={openCreate}/>
    <AdminTable loading={loading} emptyIcon="📦" emptyText="No products yet"
      cols={['Product','Category','Featured','Status','Actions']}
      rows={data.map(p=>(
        <tr key={p.id}>
          <td><div className="d-flex align-items-center gap-2">{p.cover_image&&<img src={p.cover_image} alt="" style={{width:52,height:36,objectFit:'cover',borderRadius:6,border:'1px solid #e2e8f0'}}/>}<div><div style={{fontWeight:600,fontSize:'0.88rem',color:'#334155'}}>{p.name}</div>{p.short_desc&&<div style={{fontSize:'0.73rem',color:'#64748b'}}>{p.short_desc.slice(0,40)}…</div>}</div></div></td>
          <td><span style={{background:'#e0f2fe',color:'#0284c7',borderRadius:100,padding:'0.15rem 0.65rem',fontSize:'0.72rem'}}>{p.category}</span></td>
          <td>{p.is_featured?<span style={{color:'#f5a623'}}>⭐</span>:'—'}</td>
          <td><StatusBadge active={p.is_active}/></td>
          <td><ActBtns onEdit={()=>openEdit(p)} onDelete={()=>handleDelete(p.id)}/></td>
        </tr>
      ))}/>
    <Modal show={modal} onHide={()=>setModal(false)} size="lg" centered>
      <Modal.Header closeButton><Modal.Title style={{fontFamily:'Space Grotesk, system-ui, sans-serif',fontSize:'1rem',color:'#334155'}}>{editing?'Edit':'Add'} Product</Modal.Title></Modal.Header>
      <Modal.Body style={{padding:'1.5rem'}}>
        <Row className="g-3">
          <Col sm={8}><Form.Label className="form-label">Product Name *</Form.Label><Form.Control value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Chefmax Web Suite"/></Col>
          <Col sm={4}><Form.Label className="form-label">Category *</Form.Label><Form.Select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}><option value="">Select…</option>{CATS.map(c=><option key={c}>{c}</option>)}</Form.Select></Col>
          <Col xs={12}><Form.Label className="form-label">Short Description</Form.Label><Form.Control as="textarea" rows={2} value={form.short_desc} onChange={e=>setForm(f=>({...f,short_desc:e.target.value}))}/></Col>
          <Col xs={12}>
            <Form.Label className="form-label">Full Description</Form.Label>
            <RichEditor value={form.description} onChange={val=>setForm(f=>({...f,description:val}))} />
          </Col>
          <Col sm={6}><Form.Label className="form-label">Cover Image</Form.Label>
            {editing && form.cover_image && (
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 10px',background:'#f0f9ff',border:'1px solid #e2e8f0',borderRadius:8}}>
                <img src={form.cover_image} alt="" style={{width:40,height:28,objectFit:'cover',borderRadius:4,border:'1px solid #e2e8f0'}}/>
                <span style={{fontSize:'0.78rem',color:'#0284c7',flex:1}}>Current image uploaded</span>
              </div>
            )}
            <Form.Control type="file" accept="image/*" onChange={e=>setImg(e.target.files[0])}/><div style={{fontSize:'0.72rem',color:'#94a3b8',marginTop:3}}>Leave empty to keep current image</div></Col>
          <Col sm={3}><Form.Label className="form-label">Sort Order</Form.Label><Form.Control type="number" value={form.sort_order} onChange={e=>setForm(f=>({...f,sort_order:e.target.value}))}/></Col>
          <Col sm={3} className="d-flex gap-3 align-items-end pb-1"><Form.Check label="Featured" checked={form.is_featured} onChange={e=>setForm(f=>({...f,is_featured:e.target.checked}))}/><Form.Check label="Active" checked={form.is_active} onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))}/></Col>
          <Col xs={12}><hr style={{borderColor:'rgba(255,255,255,0.08)'}}/><Form.Label className="form-label" style={{color:'#0284c7',fontSize:'0.75rem',letterSpacing:'0.1em'}}>SEO FIELDS</Form.Label></Col>
          <Col sm={6}><Form.Label className="form-label">Meta Title</Form.Label><Form.Control value={form.meta_title} onChange={e=>setForm(f=>({...f,meta_title:e.target.value}))} placeholder="SEO title…"/></Col>
          <Col sm={6}><Form.Label className="form-label">Meta Keywords</Form.Label><Form.Control value={form.meta_keywords} onChange={e=>setForm(f=>({...f,meta_keywords:e.target.value}))} placeholder="keyword1, keyword2…"/></Col>
          <Col xs={12}><Form.Label className="form-label">Meta Description</Form.Label><Form.Control as="textarea" rows={2} value={form.meta_desc} onChange={e=>setForm(f=>({...f,meta_desc:e.target.value}))} placeholder="SEO description…"/></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={()=>setModal(false)}>Cancel</Button><Button className="btn-cyber" onClick={handleSave} disabled={saving}>{saving?'Saving…':editing?'Update':'Create'}</Button></Modal.Footer>
    </Modal>
  </>);
}

// ─── News Admin ───────────────────────────────────────────────────────────────
export function NewsAdmin() {
  const [data,setData]=useState([]); const [loading,setL]=useState(true);
  const [modal,setModal]=useState(false); const [editing,setEdit]=useState(null);
  const [imgFile,setImg]=useState(null); const [saving,setSav]=useState(false);
  const EMPTY={title:'',excerpt:'',content:'',author_name:'',category:'',tags:'',is_published:false,is_featured:false,meta_title:'',meta_desc:''};
  const [form,setForm]=useState(EMPTY);

  const load=async()=>{setL(true);try{const r=await newsAPI.adminGetAll({limit:100});setData(r.data.data||[]);}catch{}setL(false);};
  useEffect(()=>{load();},[]);
  const openCreate=()=>{setEdit(null);setForm(EMPTY);setImg(null);setModal(true);};
  const openEdit=(n)=>{setEdit(n);setForm({title:n.title,excerpt:n.excerpt||'',content:n.content,author_name:n.author_name||'',category:n.category||'',tags:n.tags||'',is_published:Boolean(n.is_published),is_featured:Boolean(n.is_featured),meta_title:n.meta_title||'',meta_desc:n.meta_desc||'',cover_image:n.cover_image||''});setImg(null);setModal(true);};
  const handleSave=async()=>{
    if(!form.title||!form.content){toast.error('Title and content required');return;}
    setSav(true);
    try{
      const fd=new FormData();
      Object.entries(form).forEach(([k,v])=>{ if(k==='is_active'||k==='is_featured'){fd.append(k,v?'1':'0');}else{fd.append(k,v??'');} });
      if(imgFile)fd.append('image',imgFile);
      editing?await newsAPI.update(editing.id,fd):await newsAPI.create(fd);
      toast.success(editing?'Article updated!':'Article created!');
      setModal(false);load();
    }catch(err){toast.error(err.response?.data?.message||'Save failed');}
    setSav(false);
  };
  const handleDelete=async(id)=>{if(!window.confirm('Delete this article?'))return;try{await newsAPI.delete(id);toast.success('Deleted');load();}catch{toast.error('Failed');}};

  return(<>
    <PageHeader title="News & Blog" subtitle="Manage news articles and insights" btnLabel="Add Article" onAdd={openCreate}/>
    <AdminTable loading={loading} emptyIcon="📰" emptyText="No articles yet"
      cols={['Article','Status','Actions']}
      rows={data.map(n=>(
        <tr key={n.id}>
          <td>
            <div className="d-flex align-items-center gap-2">
              {n.cover_image&&<img src={n.cover_image} alt="" style={{width:52,height:36,objectFit:'cover',borderRadius:6,border:'1px solid #e2e8f0',flexShrink:0}}/>}
              <div style={{minWidth:0}}>
                <div style={{fontWeight:600,fontSize:'0.88rem',color:'#334155',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:280}}>{n.title}</div>
                <div style={{fontSize:'0.72rem',color:'#64748b',display:'flex',gap:8,flexWrap:'wrap',marginTop:2}}>
                  <span>{n.author_name||'—'}</span>
                  {n.category&&<span style={{color:'#0284c7'}}>{n.category}</span>}
                  {n.published_at&&<span>{new Date(n.published_at).toLocaleDateString('en-AU',{month:'short',day:'numeric',year:'numeric'})}</span>}
                </div>
              </div>
            </div>
          </td>
          <td><StatusBadge active={n.is_published}/></td>
          <td><ActBtns onEdit={()=>openEdit(n)} onDelete={()=>handleDelete(n.id)}/></td>
        </tr>
      ))}/>
    <Modal show={modal} onHide={()=>setModal(false)} size="xl" centered>
      <Modal.Header closeButton><Modal.Title style={{fontFamily:'Space Grotesk, system-ui, sans-serif',fontSize:'1rem',color:'#334155'}}>{editing?'Edit':'Add'} Article</Modal.Title></Modal.Header>
      <Modal.Body style={{padding:'1.5rem'}}>
        <Row className="g-3">
          <Col xs={12}><Form.Label className="form-label">Article Title *</Form.Label><Form.Control value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Article title…"/></Col>
          <Col sm={4}><Form.Label className="form-label">Author</Form.Label><Form.Control value={form.author_name} onChange={e=>setForm(f=>({...f,author_name:e.target.value}))}/></Col>
          <Col sm={4}><Form.Label className="form-label">Category</Form.Label><Form.Select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}><option value="">Select…</option>{['Product Updates','Industry Insights','Cyber Security','Company News','Technology'].map(c=><option key={c}>{c}</option>)}</Form.Select></Col>
          <Col sm={4}><Form.Label className="form-label">Tags (comma separated)</Form.Label><Form.Control value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder="tag1, tag2"/></Col>
          <Col xs={12}><Form.Label className="form-label">Excerpt</Form.Label><Form.Control as="textarea" rows={2} value={form.excerpt} onChange={e=>setForm(f=>({...f,excerpt:e.target.value}))}/></Col>
          <Col xs={12}><Form.Label className="form-label">Content * (HTML supported)</Form.Label><Form.Control as="textarea" rows={8} value={form.content} onChange={e=>setForm(f=>({...f,content:e.target.value}))} style={{fontFamily:'var(--font-mono)',fontSize:'0.83rem'}}/></Col>
          <Col sm={6}><Form.Label className="form-label">Cover Image</Form.Label>
            {editing && form.cover_image && (
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 10px',background:'#f0f9ff',border:'1px solid #e2e8f0',borderRadius:8}}>
                <img src={form.cover_image} alt="" style={{width:40,height:28,objectFit:'cover',borderRadius:4,border:'1px solid #e2e8f0'}}/>
                <span style={{fontSize:'0.78rem',color:'#0284c7',flex:1}}>Current image uploaded</span>
              </div>
            )}
            <Form.Control type="file" accept="image/*" onChange={e=>setImg(e.target.files[0])}/><div style={{fontSize:'0.72rem',color:'#94a3b8',marginTop:3}}>Leave empty to keep current image</div></Col>
          <Col sm={6} className="d-flex gap-4 align-items-end pb-1"><Form.Check label="Published" checked={form.is_published} onChange={e=>setForm(f=>({...f,is_published:e.target.checked}))}/><Form.Check label="Featured" checked={form.is_featured} onChange={e=>setForm(f=>({...f,is_featured:e.target.checked}))}/></Col>
          <Col sm={6}><Form.Label className="form-label">Meta Title</Form.Label><Form.Control value={form.meta_title} onChange={e=>setForm(f=>({...f,meta_title:e.target.value}))}/></Col>
          <Col sm={6}><Form.Label className="form-label">Meta Description</Form.Label><Form.Control value={form.meta_desc} onChange={e=>setForm(f=>({...f,meta_desc:e.target.value}))}/></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={()=>setModal(false)}>Cancel</Button><Button className="btn-cyber" onClick={handleSave} disabled={saving}>{saving?'Saving…':editing?'Update':'Create'}</Button></Modal.Footer>
    </Modal>
  </>);
}

// ─── Offers Admin ─────────────────────────────────────────────────────────────
export function OffersAdmin() {
  const [data,setData]=useState([]); const [loading,setL]=useState(true);
  const [modal,setModal]=useState(false); const [editing,setEdit]=useState(null);
  const [imgFile,setImg]=useState(null); const [saving,setSav]=useState(false);
  const EMPTY={title:'',subtitle:'',description:'',discount_type:'',discount_value:'',coupon_code:'',valid_from:'',valid_until:'',cta_text:'Get This Offer',cta_link:'/contact',is_featured:false,is_active:true};
  const [form,setForm]=useState(EMPTY);

  const load=async()=>{setL(true);try{const r=await offerAPI.adminGetAll();setData(r.data.data||[]);}catch{}setL(false);};
  useEffect(()=>{load();},[]);
  const openCreate=()=>{setEdit(null);setForm(EMPTY);setImg(null);setModal(true);};
  const openEdit=(o)=>{setEdit(o);setForm({title:o.title,subtitle:o.subtitle||'',description:o.description||'',discount_type:o.discount_type||'',discount_value:o.discount_value||'',coupon_code:o.coupon_code||'',valid_from:o.valid_from?.split('T')[0]||'',valid_until:o.valid_until?.split('T')[0]||'',cta_text:o.cta_text||'Get This Offer',cta_link:o.cta_link||'/contact',is_featured:Boolean(o.is_featured),is_active:Boolean(o.is_active)});setImg(null);setModal(true);};
  const handleSave=async()=>{
    if(!form.title){toast.error('Title required');return;}
    setSav(true);
    try{
      const fd=new FormData();
      Object.entries(form).forEach(([k,v])=>{
        if(k==='is_active'||k==='is_featured'){fd.append(k,v?'1':'0');}
        else if(v!==null&&v!==''){fd.append(k,v);}
      });
      if(imgFile)fd.append('image',imgFile);
      editing?await offerAPI.update(editing.id,fd):await offerAPI.create(fd);
      toast.success(editing?'Offer updated!':'Offer created!');
      setModal(false);load();
    }catch(err){toast.error(err.response?.data?.message||'Save failed');}
    setSav(false);
  };
  const handleDelete=async(id)=>{if(!window.confirm('Delete?'))return;try{await offerAPI.delete(id);toast.success('Deleted');load();}catch{toast.error('Failed');}};

  return(<>
    <PageHeader title="Offers & Promotions" subtitle="Manage special offers and promotions" btnLabel="Add Offer" onAdd={openCreate}/>
    <AdminTable loading={loading} emptyIcon="🏷️" emptyText="No offers yet"
      cols={['Offer','Status','Actions']}
      rows={data.map(o=>(
        <tr key={o.id}>
          <td>
            <div style={{minWidth:0}}>
              <div style={{fontWeight:600,fontSize:'0.88rem',color:'#334155',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:260}}>{o.title}</div>
              <div style={{fontSize:'0.72rem',color:'#64748b',display:'flex',gap:8,flexWrap:'wrap',marginTop:2}}>
                {o.discount_value&&<span style={{color:'#0284c7',fontWeight:600}}>{o.discount_type==='percent'?`${o.discount_value}% off`:o.discount_type==='fixed'?`$${o.discount_value} off`:'Bundle'}</span>}
                {o.coupon_code&&<code style={{background:'#e0f2fe',color:'#0284c7',padding:'0.1rem 0.4rem',borderRadius:4}}>{o.coupon_code}</code>}
                {o.valid_until&&<span>Exp: {new Date(o.valid_until).toLocaleDateString('en-AU',{month:'short',day:'numeric'})}</span>}
              </div>
            </div>
          </td>
          <td><StatusBadge active={o.is_active}/></td>
          <td><ActBtns onEdit={()=>openEdit(o)} onDelete={()=>handleDelete(o.id)}/></td>
        </tr>
      ))}/>
    <Modal show={modal} onHide={()=>setModal(false)} size="lg" centered>
      <Modal.Header closeButton><Modal.Title style={{fontFamily:'Space Grotesk, system-ui, sans-serif',fontSize:'1rem',color:'#334155'}}>{editing?'Edit':'Add'} Offer</Modal.Title></Modal.Header>
      <Modal.Body style={{padding:'1.5rem'}}>
        <Row className="g-3">
          <Col xs={12}><Form.Label className="form-label">Title *</Form.Label><Form.Control value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/></Col>
          <Col xs={12}><Form.Label className="form-label">Subtitle</Form.Label><Form.Control value={form.subtitle} onChange={e=>setForm(f=>({...f,subtitle:e.target.value}))}/></Col>
          <Col xs={12}><Form.Label className="form-label">Description</Form.Label><Form.Control as="textarea" rows={3} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></Col>
          <Col sm={4}><Form.Label className="form-label">Discount Type</Form.Label><Form.Select value={form.discount_type} onChange={e=>setForm(f=>({...f,discount_type:e.target.value}))}><option value="">None</option><option value="percent">Percentage (%)</option><option value="fixed">Fixed ($)</option><option value="bundle">Bundle</option></Form.Select></Col>
          <Col sm={4}><Form.Label className="form-label">Discount Value</Form.Label><Form.Control type="number" step="0.01" value={form.discount_value} onChange={e=>setForm(f=>({...f,discount_value:e.target.value}))}/></Col>
          <Col sm={4}><Form.Label className="form-label">Coupon Code</Form.Label><Form.Control value={form.coupon_code} onChange={e=>setForm(f=>({...f,coupon_code:e.target.value.toUpperCase()}))} placeholder="CODE123"/></Col>
          <Col sm={6}><Form.Label className="form-label">Valid From</Form.Label><Form.Control type="date" value={form.valid_from} onChange={e=>setForm(f=>({...f,valid_from:e.target.value}))}/></Col>
          <Col sm={6}><Form.Label className="form-label">Valid Until</Form.Label><Form.Control type="date" value={form.valid_until} onChange={e=>setForm(f=>({...f,valid_until:e.target.value}))}/></Col>
          <Col sm={6}><Form.Label className="form-label">CTA Button Text</Form.Label><Form.Control value={form.cta_text} onChange={e=>setForm(f=>({...f,cta_text:e.target.value}))}/></Col>
          <Col sm={6}><Form.Label className="form-label">CTA Link</Form.Label><Form.Control value={form.cta_link} onChange={e=>setForm(f=>({...f,cta_link:e.target.value}))}/></Col>
          <Col sm={6}><Form.Label className="form-label">Cover Image</Form.Label>
            {editing && form.cover_image && (
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 10px',background:'#f0f9ff',border:'1px solid #e2e8f0',borderRadius:8}}>
                <img src={form.cover_image} alt="" style={{width:40,height:28,objectFit:'cover',borderRadius:4,border:'1px solid #e2e8f0'}}/>
                <span style={{fontSize:'0.78rem',color:'#0284c7',flex:1}}>Current image uploaded</span>
              </div>
            )}
            <Form.Control type="file" accept="image/*" onChange={e=>setImg(e.target.files[0])}/><div style={{fontSize:'0.72rem',color:'#94a3b8',marginTop:3}}>Leave empty to keep current image</div></Col>
          <Col sm={6} className="d-flex gap-4 align-items-end pb-1"><Form.Check label="Featured" checked={form.is_featured} onChange={e=>setForm(f=>({...f,is_featured:e.target.checked}))}/><Form.Check label="Active" checked={form.is_active} onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))}/></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={()=>setModal(false)}>Cancel</Button><Button className="btn-cyber" onClick={handleSave} disabled={saving}>{saving?'Saving…':editing?'Update':'Create'}</Button></Modal.Footer>
    </Modal>
  </>);
}

// ─── Sliders Admin ────────────────────────────────────────────────────────────
export function SlidersAdmin() {
  const [data,setData]=useState([]); const [loading,setL]=useState(true);
  const [modal,setModal]=useState(false); const [editing,setEdit]=useState(null);
  const [imgFile,setImg]=useState(null); const [preview,setPreview]=useState(''); const [saving,setSav]=useState(false);
  const EMPTY={title:'',subtitle:'',description:'',cta_text:'Explore Solutions',cta_link:'/products',sort_order:0,is_active:true};
  const [form,setForm]=useState(EMPTY);

  const load=async()=>{setL(true);try{const r=await sliderAPI.getAll();setData(r.data.data||[]);}catch{}setL(false);};
  useEffect(()=>{load();},[]);
  const openCreate=()=>{setEdit(null);setForm(EMPTY);setImg(null);setPreview('');setModal(true);};
  const openEdit=(s)=>{setEdit(s);setForm({title:s.title,subtitle:s.subtitle||'',description:s.description||'',cta_text:s.cta_text||'Explore Solutions',cta_link:s.cta_link||'/products',sort_order:s.sort_order,is_active:Boolean(s.is_active)});setPreview(s.image_url||'');setImg(null);setModal(true);};
  const handleImg=(e)=>{const f=e.target.files[0];if(!f)return;setImg(f);const r=new FileReader();r.onload=ev=>setPreview(ev.target.result);r.readAsDataURL(f);};
  const handleSave=async()=>{
    if(!form.title){toast.error('Title required');return;}
    if(!editing&&!imgFile){toast.error('Image required');return;}
    setSav(true);
    try{
      const fd=new FormData();
      Object.entries(form).forEach(([k,v])=>{ if(k==='is_active'||k==='is_featured'){fd.append(k,v?'1':'0');}else{fd.append(k,v??'');} });
      if(imgFile)fd.append('image',imgFile);
      editing?await sliderAPI.update(editing.id,fd):await sliderAPI.create(fd);
      toast.success(editing?'Slider updated!':'Slider created!');
      setModal(false);load();
    }catch(err){toast.error(err.response?.data?.message||'Save failed');}
    setSav(false);
  };
  const handleDelete=async(id)=>{if(!window.confirm('Delete slider?'))return;try{await sliderAPI.delete(id);toast.success('Deleted');load();}catch{toast.error('Failed');}};

  return(<>
    <PageHeader title="Hero Sliders" subtitle="Manage homepage hero carousel" btnLabel="Add Slider" onAdd={openCreate}/>
    <AdminTable loading={loading} emptyIcon="🖼️" emptyText="No sliders yet"
      cols={['Slider','Order','Status','Actions']}
      rows={data.map(s=>(
        <tr key={s.id}>
          <td><div className="d-flex align-items-center gap-3"><img src={s.image_url} alt={s.title} style={{width:80,height:48,objectFit:'cover',borderRadius:6,border:'1px solid #e2e8f0'}} onError={e=>e.target.style.display='none'}/><div><div style={{fontWeight:600,fontSize:'0.88rem',color:'#334155'}}>{s.title}</div>{s.subtitle&&<div style={{fontSize:'0.73rem',color:'#64748b'}}>{s.subtitle.slice(0,50)}…</div>}</div></div></td>
          <td><span style={{background:'#e0f2fe',color:'#0284c7',borderRadius:100,padding:'0.15rem 0.65rem',fontSize:'0.78rem',fontWeight:600}}>{s.sort_order}</span></td>
          <td><StatusBadge active={s.is_active}/></td>
          <td><ActBtns onEdit={()=>openEdit(s)} onDelete={()=>handleDelete(s.id)}/></td>
        </tr>
      ))}/>
    <Modal show={modal} onHide={()=>setModal(false)} size="lg" centered>
      <Modal.Header closeButton><Modal.Title style={{fontFamily:'Space Grotesk, system-ui, sans-serif',fontSize:'1rem',color:'#334155'}}>{editing?'Edit':'Add'} Slider</Modal.Title></Modal.Header>
      <Modal.Body style={{padding:'1.5rem'}}>
        <Row className="g-3">
          <Col xs={12}>
            <Form.Label className="form-label">Slider Image {!editing&&'*'}</Form.Label>
            <div style={{border:'2px dashed rgba(0,212,255,0.2)',borderRadius:'12px',padding:'1.5rem',textAlign:'center',cursor:'pointer',background:'rgba(0,212,255,0.02)'}} onClick={()=>document.getElementById('sl-img').click()} onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(0,212,255,0.4)'} onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(0,212,255,0.2)'}>
              {preview?<img src={preview} alt="preview" style={{maxHeight:160,maxWidth:'100%',borderRadius:8,objectFit:'cover'}}/>:<div style={{color:'#64748b',fontSize:'0.88rem'}}>Click to upload image<br/><small>Recommended: 1920×1080px</small></div>}
              <input id="sl-img" type="file" accept="image/*" onChange={handleImg} style={{display:'none'}}/>
            </div>
          </Col>
          <Col sm={8}><Form.Label className="form-label">Title *</Form.Label><Form.Control value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/></Col>
          <Col sm={4}><Form.Label className="form-label">Sort Order</Form.Label><Form.Control type="number" value={form.sort_order} onChange={e=>setForm(f=>({...f,sort_order:e.target.value}))}/></Col>
          <Col xs={12}><Form.Label className="form-label">Subtitle</Form.Label><Form.Control value={form.subtitle} onChange={e=>setForm(f=>({...f,subtitle:e.target.value}))}/></Col>
          <Col xs={12}><Form.Label className="form-label">Description</Form.Label><Form.Control as="textarea" rows={2} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></Col>
          <Col sm={4}><Form.Label className="form-label">CTA Button Text</Form.Label><Form.Control value={form.cta_text} onChange={e=>setForm(f=>({...f,cta_text:e.target.value}))}/></Col>
          <Col sm={4}><Form.Label className="form-label">CTA Link</Form.Label><Form.Control value={form.cta_link} onChange={e=>setForm(f=>({...f,cta_link:e.target.value}))}/></Col>
          <Col sm={4} className="d-flex align-items-end pb-1"><Form.Check type="switch" label="Active" checked={form.is_active} onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))}/></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={()=>setModal(false)}>Cancel</Button><Button className="btn-cyber" onClick={handleSave} disabled={saving}>{saving?'Saving…':editing?'Update':'Create'}</Button></Modal.Footer>
    </Modal>
  </>);
}

// ─── Testimonials Admin ───────────────────────────────────────────────────────
export function TestimonialsAdmin() {
  const [data,setData]=useState([]); const [loading,setL]=useState(true);
  const load=async()=>{setL(true);try{const r=await testimonialAPI.getAdmin();setData(r.data.data||[]);}catch{}setL(false);};
  useEffect(()=>{load();},[]);
  const toggle=async(t,field)=>{try{await testimonialAPI.update(t.id,{is_approved:field==='approved'?!t.is_approved:Boolean(t.is_approved),is_featured:field==='featured'?!t.is_featured:Boolean(t.is_featured)});load();}catch{}};
  const del=async(id)=>{if(!window.confirm('Delete?'))return;try{await testimonialAPI.delete(id);toast.success('Deleted');load();}catch{}};
  return(<>
    <PageHeader title="Testimonials" subtitle="Approve and feature client reviews"/>
    <AdminTable loading={loading} emptyIcon="⭐" emptyText="No testimonials yet"
      cols={['Client','Company','Rating','Review','Approved','Featured','Actions']}
      rows={data.map(t=>(
        <tr key={t.id}>
          <td><div style={{fontWeight:600,fontSize:'0.88rem',color:'#334155'}}>{t.author_name}</div>{t.author_title&&<div style={{fontSize:'0.73rem',color:'#64748b'}}>{t.author_title}</div>}</td>
          <td style={{fontSize:'0.82rem',color:'#64748b'}}>{t.author_company||'—'}</td>
          <td><span style={{color:'#f5a623'}}>{'★'.repeat(t.rating)}</span></td>
          <td style={{maxWidth:240,fontSize:'0.78rem',color:'#64748b'}}>{t.content.slice(0,80)}…</td>
          <td><button onClick={()=>toggle(t,'approved')} style={{border:'none',background:'none',cursor:'pointer',fontSize:'1.1rem'}}>{t.is_approved?'✅':'❌'}</button></td>
          <td><button onClick={()=>toggle(t,'featured')} style={{border:'none',background:'none',cursor:'pointer',fontSize:'1.1rem'}}>{t.is_featured?'⭐':'☆'}</button></td>
          <td><ActBtns onDelete={()=>del(t.id)}/></td>
        </tr>
      ))}/>
  </>);
}

// ─── Subscribers Admin ────────────────────────────────────────────────────────
export function SubscribersAdmin() {
  const [data,setData]=useState([]); const [loading,setL]=useState(true);
  const load=async()=>{setL(true);try{const r=await adminAPI.getSubscribers();setData(r.data.data||[]);}catch{}setL(false);};
  useEffect(()=>{load();},[]);

  const toggleActive=async(s)=>{
    try{
      await adminAPI.toggleSubscriber(s.id);
      toast.success(s.is_active?'Subscriber deactivated':'Subscriber activated');
      load();
    }catch{toast.error('Failed to update');}
  };

  const handleDelete=async(s)=>{
    if(!window.confirm(`Remove ${s.email} from subscribers?`))return;
    try{
      await adminAPI.deleteSubscriber(s.id);
      toast.success('Subscriber removed');
      load();
    }catch{toast.error('Failed to delete');}
  };

  const exportCSV=()=>{const rows=['Email,Name,Status,Subscribed Date',...data.map(s=>`${s.email},${s.full_name||''},${s.is_active?'Active':'Inactive'},${new Date(s.subscribed_at).toLocaleDateString()}`)];const blob=new Blob([rows.join('\n')],{type:'text/csv'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download='subscribers.csv';a.click();};

  return(<>
    <div className="d-flex justify-content-between align-items-start mb-4">
      <div><h4 style={{fontFamily:'Space Grotesk, system-ui, sans-serif',color:'#334155',marginBottom:'0.2rem'}}>Newsletter Subscribers</h4><p style={{color:'#64748b',fontSize:'0.88rem',margin:0}}>{data.filter(s=>s.is_active).length} active · {data.filter(s=>!s.is_active).length} inactive</p></div>
      <Button onClick={exportCSV} style={{borderRadius:8,fontSize:'0.85rem',background:'#0066ff',color:'#fff',border:'none',padding:'0.5rem 1.2rem',display:'flex',alignItems:'center',gap:8,fontWeight:600}}><FaDownload/> Export CSV</Button>
    </div>
    <AdminTable loading={loading} emptyIcon="📧" emptyText="No subscribers yet"
      cols={['Email','Name','Status','Subscribed','Actions']}
      rows={data.map(s=>(
        <tr key={s.id}>
          <td style={{color:'#334155',fontSize:'0.88rem'}}>{s.email}</td>
          <td style={{fontSize:'0.85rem',color:'#64748b'}}>{s.full_name||'—'}</td>
          <td><StatusBadge active={s.is_active}/></td>
          <td style={{fontSize:'0.78rem',color:'#64748b'}}>{new Date(s.subscribed_at).toLocaleDateString('en-AU',{month:'short',day:'numeric',year:'numeric'})}</td>
          <td>
            <div className="d-flex gap-2">
              <button
                onClick={()=>toggleActive(s)}
                title={s.is_active?'Deactivate':'Activate'}
                style={{border:'none',background:s.is_active?'rgba(239,68,68,0.1)':'rgba(34,197,94,0.1)',color:s.is_active?'#ef4444':'#22c55e',borderRadius:6,padding:'4px 10px',fontSize:'0.78rem',fontWeight:600,cursor:'pointer'}}>
                {s.is_active?'Deactivate':'Activate'}
              </button>
              <button
                onClick={()=>handleDelete(s)}
                title="Remove subscriber"
                style={{border:'none',background:'rgba(239,68,68,0.08)',color:'#ef4444',borderRadius:6,padding:'4px 8px',fontSize:'0.78rem',cursor:'pointer'}}>
                <FaTrash/>
              </button>
            </div>
          </td>
        </tr>
      ))}/>
  </>);
}

// ─── Settings Admin ───────────────────────────────────────────────────────────
export function SettingsAdmin() {
  const [settings,setSettings]=useState({});
  const [loading,setL]=useState(true);
  const [saving,setSav]=useState(false);
  const [logoFile,setLogoFile]=useState(null);
  const [logoPreview,setLogoPrev]=useState('');
  const [logoUploading,setLogoUp]=useState(false);

  useEffect(()=>{
    adminAPI.getSettings().then(r=>{
      const s=r.data.data||{};
      setSettings(s);
      if(s.logo_url) setLogoPrev(resolveUrl(s.logo_url));
    }).catch(()=>{}).finally(()=>setL(false));
  },[]);

  const handleLogoChange=(e)=>{
    const file=e.target.files[0];
    if(!file) return;
    setLogoFile(file);
    const reader=new FileReader();
    reader.onload=ev=>setLogoPrev(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload=async()=>{
    if(!logoFile){toast.error('Select a logo file first');return;}
    setLogoUp(true);
    try{
      const fd=new FormData();
      fd.append('logo',logoFile);
      console.log('Uploading logo:', logoFile.name, logoFile.type, logoFile.size);
      const r=await adminAPI.uploadLogo(fd);
      console.log('Upload response:', r.data);
      const savedUrl = r.data.logo_url;
      setSettings(s=>({...s,logo_url:savedUrl}));
      setLogoPrev(savedUrl);
      setLogoFile(null);
      toast.success('Logo uploaded! URL: ' + savedUrl);
    }catch(err){
      console.error('Logo upload error:', err.response?.data || err.message);
      toast.error(err.response?.data?.message||'Upload failed');
    }
    setLogoUp(false);
  };

  const handleSave=async()=>{
    setSav(true);
    try{await adminAPI.updateSettings(settings);toast.success('Settings saved!');}
    catch{toast.error('Save failed');}
    setSav(false);
  };

  if(loading)return<div className="text-center py-5"><Spinner animation="border" style={{color:'#060d1a'}}/></div>;

  const FIELDS=[
    ['site_name','Site Name','text'],['site_tagline','Tagline','text'],
    ['contact_email','Contact Email','email'],['marketing_email','Marketing Email','email'],
    ['contact_phone','Phone Number','text'],['contact_address','Address','text'],
    ['years_experience','Years Experience','text'],['happy_clients','Happy Clients Count','text'],
    ['projects_delivered','Projects Delivered','text'],['facebook_url','Facebook URL','url'],
    ['linkedin_url','LinkedIn URL','url'],['twitter_url','Twitter/X URL','url'],
    ['instagram_url','Instagram URL','url'],['youtube_url','YouTube URL','url'],
    ['meta_title','Meta Title (SEO)','text'],['meta_keywords','Meta Keywords (SEO)','text'],
  ];

  return(<>
    <PageHeader title="Site Settings" subtitle="Configure website information, logo and SEO"/>

    {/* Logo Upload */}
    <div style={{background:'white',border:'1px solid var(--border-grey)',borderRadius:'12px',padding:'2rem',marginBottom:'1.5rem',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
      <h5 style={{fontFamily:'Space Grotesk, system-ui, sans-serif',color:'var(--text)',marginBottom:'0.25rem'}}>Site Logo</h5>
      <p style={{color:'#64748b',fontSize:'0.85rem',marginBottom:'1.5rem'}}>Upload your company logo. PNG or WebP with transparent background recommended.</p>
      <Row className="g-3 align-items-end">
        <Col xs={12} sm="auto">
          <div style={{width:200,height:80,border:'2px dashed var(--border-grey)',borderRadius:'8px',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--page-bg)',overflow:'hidden',cursor:'pointer'}}
               onClick={()=>document.getElementById('logo-input').click()}>
            {logoPreview
              ? <img src={logoPreview} alt="Logo preview" style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain',padding:'8px'}}/>
              : <div style={{textAlign:'center',color:'#64748b',fontSize:'0.8rem'}}><div style={{fontSize:'1.5rem',marginBottom:4}}>🖼️</div><div>Click to select</div></div>
            }
          </div>
          <input id="logo-input" type="file" accept="image/*" onChange={handleLogoChange} style={{display:'none'}}/>
        </Col>
        <Col>
          <div style={{marginBottom:8}}>
            <Button variant="outline-secondary" size="sm" onClick={()=>document.getElementById('logo-input').click()} style={{marginRight:8}}>Choose File</Button>
            <Button className="btn-cyber" size="sm" onClick={handleLogoUpload} disabled={logoUploading||!logoFile}>
              {logoUploading?<><Spinner animation="border" size="sm" className="me-2"/>Uploading…</>:'Upload Logo'}
            </Button>
          </div>
          {settings.logo_url
            ? <p style={{fontSize:'0.78rem',color:'green',margin:0}}>✅ Logo active</p>
            : <p style={{fontSize:'0.78rem',color:'#64748b',margin:0}}>No custom logo — using default</p>
          }
        </Col>
      </Row>
    </div>

    {/* General Settings */}
    <div style={{background:'white',border:'1px solid var(--border-grey)',borderRadius:'12px',padding:'2rem',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
      <h5 style={{fontFamily:'Space Grotesk, system-ui, sans-serif',color:'var(--text)',marginBottom:'1.5rem'}}>General Settings</h5>
      <Row className="g-3">
        {FIELDS.map(([key,label,type])=>(
          <Col key={key} sm={6}><Form.Label className="form-label">{label}</Form.Label><Form.Control type={type} value={settings[key]||''} onChange={e=>setSettings(s=>({...s,[key]:e.target.value}))}/></Col>
        ))}
        <Col xs={12}><Form.Label className="form-label">Site Description (SEO)</Form.Label><Form.Control as="textarea" rows={3} value={settings.meta_description||''} onChange={e=>setSettings(s=>({...s,meta_description:e.target.value}))}/></Col>
        <Col xs={12}><Form.Label className="form-label">About Text</Form.Label><Form.Control as="textarea" rows={3} value={settings.site_description||''} onChange={e=>setSettings(s=>({...s,site_description:e.target.value}))}/></Col>
      </Row>
      <div className="mt-4"><Button className="btn-cyber px-4" onClick={handleSave} disabled={saving}>{saving?'Saving…':'💾 Save All Settings'}</Button></div>
    </div>
  </>);
}